import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  LockClosedIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { userChatsAreAlwaysPublic } from 'config'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  AiArtifact,
  AiConnector,
  AiConversation,
  AiKnowledgeItem,
  AiProject,
  AiSchedule,
  createAiProjectConversation,
  deleteAiConversation,
  deleteAiProject,
  getAiConnectors,
  getAiProject,
  getAiProjectArtifact,
  getAiProjectArtifacts,
  getAiProjectConversations,
  getAiProjectKnowledge,
  getAiProjectSchedules,
  getProjectConnectors,
  setProjectConnectors,
  updateAiConversation,
  updateAiProject,
} from 'services'
import { Button, Input2, Modal, TextArea } from 'stories/components'
import { formatTime } from 'utils'

import { ArtifactsPanel } from './ArtifactsPanel'
import { ArtifactViewer } from './ArtifactViewer'
import { KnowledgePanel } from './KnowledgePanel'
import { ProjectChat } from './ProjectChat'
import { RunProjectButton } from './RunProjectButton'
import { SchedulesPanel } from './SchedulesPanel'
import { SharePanel } from './SharePanel'

type SidePanel = 'knowledge' | 'artifacts' | 'schedules'

interface ProjectWorkspaceProps {
  /** Set when embedded in a loan screen; otherwise the id comes from the route. */
  projectId?: number
  /**
   * The loan this workspace is scoped to. Conversations are then that loan's alone, artifacts the
   * assistant saves are filed under it, and every turn tells the assistant which loan it is in.
   */
  loanNumber?: number
  /** Drop the page chrome (back link, page padding) so the workspace sits inside a loan screen. */
  embedded?: boolean
}

/**
 * A project workspace: conversations on the left, the chat in the middle, and the project's
 * knowledge and artifacts on the right. The assistant in the middle pane runs with this project's
 * instructions and knowledge loaded, and with the Nexys tools it already had.
 *
 * The same component serves the standalone /ai-projects/:projectId page and the in-loan workspace;
 * a loan number is the only difference, which is what keeps the two from drifting apart.
 */
export default function ProjectWorkspace({ projectId: projectIdProp, loanNumber, embedded }: ProjectWorkspaceProps = {}) {
  const navigate = useHistory()
  const { projectId: projectIdParam } = useParams<{ projectId: string }>()
  const projectId = projectIdProp || Number(projectIdParam)

  const [isLoading, setLoading] = useState(true)
  const [project, setProject] = useState<AiProject | null>(null)
  const [conversations, setConversations] = useState<AiConversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null)
  /** A Run the header asked for, waiting on the chat below to send it. */
  const [askedPrompt, setAskedPrompt] = useState<{ text: string; nonce: number } | null>(null)
  const [knowledge, setKnowledge] = useState<AiKnowledgeItem[]>([])
  const [artifacts, setArtifacts] = useState<AiArtifact[]>([])
  const [schedules, setSchedules] = useState<AiSchedule[]>([])
  const [panel, setPanel] = useState<SidePanel>('knowledge')
  const [openArtifact, setOpenArtifact] = useState<AiArtifact | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [settings, setSettings] = useState({ name: '', description: '', instructions: '', isShared: false })
  const [savingSettings, setSavingSettings] = useState(false)
  const [connectors, setConnectors] = useState<AiConnector[]>([])
  const [projectConnectorIds, setProjectConnectorIds] = useState<number[]>([])

  const profile = useSelector((state: any) => state.auth?.profile)
  const userEmail = profile?.email || ''
  const chatsStayPublic = userChatsAreAlwaysPublic(profile)
  // Only the owner may rename, reshare, or delete a project; everyone it is shared with can use it.
  const isOwner = useMemo(
    () => !!project && String(project.createdBy || '').toLowerCase() === String(userEmail).toLowerCase(),
    [project, userEmail],
  )

  const reloadKnowledge = useCallback(() => {
    getAiProjectKnowledge(projectId)
      .then((data) => setKnowledge(data || []))
      .catch(() => {})
  }, [projectId])

  const reloadArtifacts = useCallback(() => {
    getAiProjectArtifacts(projectId, loanNumber)
      .then((data) => setArtifacts(data || []))
      .catch(() => {})
  }, [projectId, loanNumber])

  const reloadSchedules = useCallback(() => {
    getAiProjectSchedules(projectId, loanNumber)
      .then((data) => setSchedules(data || []))
      .catch(() => {})
  }, [projectId, loanNumber])

  const reloadConversations = useCallback(
    async (selectFirst = false) => {
      const data = await getAiProjectConversations(projectId, loanNumber).catch(() => [] as AiConversation[])
      setConversations(data || [])
      if (selectFirst) setActiveConversationId(data?.[0]?.id ?? null)
      return data
    },
    [projectId, loanNumber],
  )

  useEffect(() => {
    if (!projectId) return
    setLoading(true)
    Promise.all([getAiProject(projectId), reloadConversations(true)])
      .then(([loaded]) => {
        setProject(loaded)
        setSettings({
          name: loaded.name,
          description: loaded.description || '',
          instructions: loaded.instructions || '',
          isShared: !!loaded.isShared,
        })
      })
      .catch(() => embedded || navigate.push('/ai-projects'))
      .finally(() => setLoading(false))
    reloadKnowledge()
    reloadArtifacts()
    reloadSchedules()
    // Connectors an admin has enabled for the company, plus this project's own selection.
    getAiConnectors()
      .then((data) => setConnectors((data || []).filter((connector) => connector.isEnabled)))
      .catch(() => setConnectors([]))
    getProjectConnectors(projectId)
      .then((ids) => setProjectConnectorIds(ids || []))
      .catch(() => setProjectConnectorIds([]))
  }, [projectId, embedded, navigate, reloadConversations, reloadKnowledge, reloadArtifacts, reloadSchedules])

  const onNewConversation = async () => {
    const conversation = await createAiProjectConversation(projectId, undefined, loanNumber)
    setConversations((prev) => [conversation, ...prev])
    setActiveConversationId(conversation.id)
  }

  /**
   * Run the project from the header: a turn in the chat on the right, which is where the thread,
   * the live steps and Stop already are. A workspace with no chat open gets one first - pressing
   * Run should not land on the "no conversation open" panel having done nothing.
   */
  const onRunProject = async (prompt: string) => {
    if (!activeConversationId) await onNewConversation()
    setAskedPrompt({ text: prompt, nonce: Date.now() })
  }

  const onToggleConversationVisibility = async (conversation: AiConversation) => {
    const visibility = conversation.visibility === 'private' ? 'public' : 'private'
    const updated = await updateAiConversation(conversation.id, { visibility }).catch(() => null)
    if (updated) setConversations((prev) => prev.map((item) => (item.id === conversation.id ? { ...item, ...updated } : item)))
  }

  const onDeleteConversation = async (conversation: AiConversation) => {
    if (!window.confirm(`Delete "${conversation.title}"?`)) return
    await deleteAiConversation(conversation.id)
    const remaining = conversations.filter((item) => item.id !== conversation.id)
    setConversations(remaining)
    if (activeConversationId === conversation.id) setActiveConversationId(remaining[0]?.id ?? null)
  }

  const panelCounts: Record<SidePanel, number> = {
    knowledge: knowledge.length,
    artifacts: artifacts.length,
    schedules: schedules.length,
  }

  /**
   * Open the chat a scheduled run wrote into. The rail is reloaded first: a run that happened while
   * this page was open - or overnight - leaves a thread the rail has never seen.
   */
  const onOpenScheduledConversation = async (conversationId: number) => {
    const data = await reloadConversations()
    if ((data || []).some((conversation) => conversation.id === conversationId)) {
      setActiveConversationId(conversationId)
    } else {
      toast('That chat is not in this view - it may belong to another loan.', { type: 'info' })
    }
  }

  const onOpenArtifact = async (artifactId: number) => {
    const artifact = await getAiProjectArtifact(projectId, artifactId).catch(() => null)
    if (artifact) setOpenArtifact(artifact)
  }

  const onSaveSettings = async () => {
    if (!settings.name.trim()) {
      toast('Give the project a name.', { type: 'error' })
      return
    }
    setSavingSettings(true)
    try {
      const updated = await updateAiProject(projectId, settings)
      await setProjectConnectors(projectId, projectConnectorIds)
      setProject(updated)
      setShowSettings(false)
    } finally {
      setSavingSettings(false)
    }
  }

  const onDeleteProject = async () => {
    if (!window.confirm(`Delete the project "${project?.name}" and everything in it? This cannot be undone.`)) return
    await deleteAiProject(projectId)
    navigate.push('/ai-projects')
  }

  return (
    <div className={`AiProjectWorkspace-container ${embedded ? '' : 'py-4 px-2'}`}>
      <div className={embedded ? '' : 'max-w-screen-2xl m-auto'}>
        <LayoutLoading show={isLoading} />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {!embedded && (
              <button
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
                onClick={() => navigate.push('/ai-projects')}
                aria-label="Back to projects"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            )}
            <div className="min-w-0">
              <h1 className={`font-bold truncate ${embedded ? 'text-base' : 'text-xl'}`}>
                {project?.name || 'Project'}
              </h1>
              {!!project?.description && <p className="text-sm text-gray-500 truncate">{project.description}</p>}
            </div>
            {!!loanNumber && (
              <span className="shrink-0 rounded-full bg-shade-blue/10 text-shade-blue text-xs font-semibold px-2.5 py-1">
                Loan {loanNumber}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <RunProjectButton
              projectId={projectId}
              loanNumber={loanNumber}
              onOpenConversation={onOpenScheduledConversation}
              onStarted={reloadSchedules}
              onRunProject={onRunProject}
            />
            <Button size="sm" color="gray" onClick={() => setShowSettings(true)}>
              <span className="flex items-center gap-1.5">
                <Cog6ToothIcon className="w-4 h-4" />
                {isOwner ? 'Project settings' : 'Project info'}
              </span>
            </Button>
          </div>
        </div>

        <div className={`grid grid-cols-12 gap-4 min-h-[36rem] ${embedded ? 'h-[calc(100vh-22rem)]' : 'h-[calc(100vh-14rem)]'}`}>
          {/* Conversations */}
          <div className="col-span-12 lg:col-span-2 border border-gray-200 rounded-lg bg-white flex flex-col min-h-0">
            <div className="px-3 py-3 border-b border-gray-200">
              <Button size="sm" onClick={onNewConversation}>
                <span className="flex items-center gap-1.5">
                  <PlusIcon className="w-4 h-4" />
                  New chat
                </span>
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {!conversations.length && <p className="text-xs text-gray-500 p-2">No conversations yet.</p>}
              {conversations.map((conversation) => {
                const isThreadOwner = conversation.createdBy === userEmail
                const isPrivate = conversation.visibility === 'private'
                return (
                  <div
                    key={conversation.id}
                    className={`group flex items-center gap-1 rounded-md px-2 py-1.5 cursor-pointer ${
                      conversation.id === activeConversationId ? 'bg-shade-blue/10' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveConversationId(conversation.id)}
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-800 truncate">{conversation.title}</p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {formatTime(conversation.lastMessageAt || conversation.createdAt)}
                        {!isThreadOwner && !!conversation.createdBy && ` · ${conversation.createdBy.split('@')[0]}`}
                      </p>
                    </div>
                    {isThreadOwner ? (
                      <>
                        {chatsStayPublic ? (
                          <span
                            className="p-0.5 text-gray-400"
                            title="Public - chats from your account stay visible to your team."
                          >
                            <GlobeAltIcon className="w-3.5 h-3.5" />
                          </span>
                        ) : (
                          <button
                            className={`p-0.5 hover:text-shade-blue ${
                              isPrivate ? 'text-gray-500' : 'opacity-0 group-hover:opacity-100 text-gray-400'
                            }`}
                            title={
                              isPrivate
                                ? 'Private - only you can see this chat. Click to make it public.'
                                : 'Public - your team can see this chat. Click to make it private.'
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleConversationVisibility(conversation)
                            }}
                          >
                            {isPrivate ? <LockClosedIcon className="w-3.5 h-3.5" /> : <GlobeAltIcon className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-600"
                          title="Delete conversation"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDeleteConversation(conversation)
                          }}
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <span className="p-0.5 text-gray-300" title={`Shared by ${conversation.createdBy}`}>
                        <GlobeAltIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Chat */}
          <div className="col-span-12 lg:col-span-6 border border-gray-200 rounded-lg bg-white flex flex-col min-h-0">
            {activeConversationId ? (
              <ProjectChat
                key={activeConversationId}
                projectId={projectId}
                conversationId={activeConversationId}
                loanNumber={loanNumber}
                artifacts={artifacts}
                onArtifactsChanged={reloadArtifacts}
                onKnowledgeChanged={reloadKnowledge}
                onOpenArtifact={onOpenArtifact}
                onConversationChanged={() => reloadConversations()}
                injectedPrompt={askedPrompt || undefined}
                onInjectedPromptSent={() => setAskedPrompt(null)}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-500 p-6">
                <ChatBubbleLeftRightIcon className="w-10 h-10 text-gray-300 mb-3" />
                <p className="font-semibold text-gray-700">No conversation open</p>
                <p className="text-sm mt-1">
                  {loanNumber
                    ? `Start a new chat to work this project on loan ${loanNumber}.`
                    : 'Start a new chat to work in this project.'}
                </p>
                <div className="mt-4">
                  <Button size="sm" onClick={onNewConversation}>
                    New chat
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Knowledge / artifacts */}
          <div className="col-span-12 lg:col-span-4 border border-gray-200 rounded-lg bg-white flex flex-col min-h-0">
            <div className="flex border-b border-gray-200">
              {(['knowledge', 'artifacts', 'schedules'] as SidePanel[]).map((tab) => (
                <button
                  key={tab}
                  className={`flex-1 px-3 py-2.5 text-sm font-medium capitalize ${
                    panel === tab
                      ? 'text-shade-blue border-b-2 border-shade-blue'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setPanel(tab)}
                >
                  {tab} ({panelCounts[tab]})
                </button>
              ))}
            </div>
            {panel === 'knowledge' && (
              <KnowledgePanel projectId={projectId} items={knowledge} onChanged={reloadKnowledge} />
            )}
            {panel === 'artifacts' && (
              <ArtifactsPanel
                projectId={projectId}
                loanNumber={loanNumber}
                artifacts={artifacts}
                onChanged={reloadArtifacts}
                onOpen={onOpenArtifact}
              />
            )}
            {panel === 'schedules' && (
              <SchedulesPanel
                projectId={projectId}
                loanNumber={loanNumber}
                schedules={schedules}
                isProjectOwner={isOwner}
                userEmail={userEmail}
                onChanged={reloadSchedules}
                onOpenConversation={onOpenScheduledConversation}
              />
            )}
          </div>
        </div>

        {openArtifact && (
          <ArtifactViewer
            projectId={projectId}
            artifact={openArtifact}
            onClose={() => setOpenArtifact(null)}
            onSaved={() => {
              reloadArtifacts()
              onOpenArtifact(openArtifact.id)
            }}
          />
        )}

        {showSettings && (
          <Modal
            title={isOwner ? 'Project settings' : 'Project info'}
            titleOkay={isOwner ? 'Save' : ''}
            isOpen
            loading={savingSettings}
            onOk={onSaveSettings}
            onClose={() => setShowSettings(false)}
          >
            <div className="w-[34rem] max-w-full">
              <Input2
                title="Name"
                required
                disabled={!isOwner}
                value={settings.name}
                onChange={(value: string) => setSettings({ ...settings, name: value })}
              />
              <div className="mt-4">
                <Input2
                  title="Description"
                  disabled={!isOwner}
                  value={settings.description}
                  onChange={(value: string) => setSettings({ ...settings, description: value })}
                />
              </div>
              <div className="mt-4">
                <TextArea
                  title="Project instructions"
                  rows={8}
                  disabled={!isOwner}
                  value={settings.instructions}
                  onChange={(value: string) => setSettings({ ...settings, instructions: value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Applied to every conversation in this project, alongside its knowledge.
                </p>
              </div>
              {!!connectors.length && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Connectors</p>
                  <p className="text-xs text-gray-500 mb-1.5">
                    Other systems this project&apos;s assistant may use. Ones marked available everywhere are on for
                    every project and cannot be switched off here.
                  </p>
                  <div className="space-y-1.5">
                    {connectors.map((connector) => (
                      <label
                        key={connector.id}
                        className={`flex items-start gap-2 text-sm ${
                          connector.isAlwaysOn ? 'text-gray-500' : 'text-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          disabled={connector.isAlwaysOn}
                          checked={connector.isAlwaysOn || projectConnectorIds.includes(connector.id)}
                          onChange={(e) =>
                            setProjectConnectorIds((prev) =>
                              e.target.checked ? [...prev, connector.id] : prev.filter((id) => id !== connector.id),
                            )
                          }
                        />
                        <span>
                          {connector.name}
                          {connector.isAlwaysOn && <span className="ml-1 text-xs">(available everywhere)</span>}
                          {connector.status !== 'connected' && (
                            <span className="ml-1 text-xs text-amber-700">(not connected)</span>
                          )}
                          {!!connector.description && (
                            <span className="block text-xs text-gray-500">{connector.description}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
                <input
                  type="checkbox"
                  disabled={!isOwner}
                  checked={settings.isShared}
                  onChange={(e) => setSettings({ ...settings, isShared: e.target.checked })}
                />
                Share with everyone in the company
              </label>
              <div className="mt-5 pt-4 border-t border-gray-200">
                <SharePanel projectId={projectId} isOwner={isOwner} isSharedWithCompany={settings.isShared} />
              </div>
              {isOwner && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button className="text-sm text-red-600 hover:underline" onClick={onDeleteProject}>
                    Delete this project
                  </button>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </div>
  )
}
